import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'

import {Colors} from '../index.js'
import {FieldProps} from './types.js'
import {componentMargins} from './styles.js'
import {controlLabel} from './choiceStyles.js'
import ControlLabel from './ControlLabel.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'

ensureFocusStyles()

type DragHandle = 'dragHandle' | 'dragHandle2'

export interface SliderProps extends FieldProps {
    value1: number
    value2: number
    min: number
    max: number
    step: number
    diff: number
    onChange?: (value1: number, value2: number) => void
    valuesToLabel: (value1: number, value2: number) => {label1: string, label2: string}
    value1Icon?: string
    value2Icon?: string
    // Clicking a handle without dragging it, a change too so not called when disabled or readOnly
    onClickValue1?: () => void
    onClickValue2?: () => void
    startInclude?: boolean
    endInclude?: boolean
    /** Accessible names of the two handles, e.g. ['Begintijd', 'Eindtijd'] */
    handleLabels?: [string, string]
}

interface SliderState {
    left1: number
    left2: number
    value1: number
    value2: number
    cursor: 'grab' | 'grabbing'
    isMoving: boolean
}

const Slider = (props: SliderProps) => {
    const {valuesToLabel, value1Icon, value2Icon, onClickValue1, onClickValue2, startInclude, endInclude, label, info, disabled, readOnly, style, handleLabels} = props

    // readOnly ignores the mouse like disabled, but is shown normally
    const interactive = !disabled && !readOnly

    const [state, setFullState] = useState<SliderState>({
        left1: 0,
        left2: 0,
        value1: 0,
        value2: 0,
        cursor: 'grab',
        isMoving: false
    })
    const setState = (partial: Partial<SliderState>) => setFullState((prev) => ({...prev, ...partial}))

    const slideBar = useRef<HTMLDivElement>(null)
    const dragHandle = useRef<HTMLDivElement>(null)
    const dragHandle2 = useRef<HTMLDivElement>(null)

    // The document mouse handlers outlive the render they were created in, so they read the latest props and state through a ref
    const latest = useRef({props, state})
    latest.current = {props, state}

    // The last cursor x position during a drag, and the document listeners of the drag in progress
    const lastClientX = useRef(0)
    const dragListeners = useRef<{onMouseMove: (event: MouseEvent) => void, onMouseUp: () => void} | null>(null)

    const valueToLeft = (value: number) => {
        const {min, max} = latest.current.props
        return ((value - min) * (slideBar.current.clientWidth - 20)) / (max - min)
    }

    // Layout effect so the handles are positioned before the first paint, like componentDidMount/componentDidUpdate did
    useLayoutEffect(() => {
        const {value1, value2} = props

        setState({left1: valueToLeft(value1), left2: valueToLeft(value2), value1, value2})
    }, [props.value1, props.value2, props.min, props.max])

    // The handle positions depend on the width of the bar
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => {
            const {value1, value2} = latest.current.state
            setState({left1: valueToLeft(value1), left2: valueToLeft(value2)})
        })
        observer.observe(slideBar.current)

        return () => observer.disconnect()
    }, [])

    const removeDragListeners = () => {
        if (dragListeners.current) {
            document.removeEventListener('mousemove', dragListeners.current.onMouseMove)
            document.removeEventListener('mouseup', dragListeners.current.onMouseUp)
            dragListeners.current = null
        }
    }

    useEffect(() => removeDragListeners, [])

    const dragMouseDown = (handle: DragHandle, event: React.MouseEvent<HTMLDivElement>) => {
        removeDragListeners()

        if (!interactive) {
            return
        }

        // get the mouse cursor position at startup:
        lastClientX.current = event.clientX

        // follow the cursor until the mouse button is released
        dragListeners.current = {
            onMouseMove: (event: MouseEvent) => elementDrag(handle, event),
            onMouseUp: closeDragElement
        }
        document.addEventListener('mousemove', dragListeners.current.onMouseMove)
        document.addEventListener('mouseup', dragListeners.current.onMouseUp)

        setState({cursor: 'grabbing'})
    }

    const elementDrag = (handle: DragHandle, event: MouseEvent) => {
        const {props, state} = latest.current

        const {isMoving} = state
        if (!isMoving) {
            setState({isMoving: true})
        }
        const {min, max, step, diff} = props
        // calculate the new cursor position:
        const deltaX = lastClientX.current - event.clientX
        lastClientX.current = event.clientX

        const handleElement = handle === 'dragHandle' ? dragHandle.current : dragHandle2.current

        const left = Math.min(slideBar.current.clientWidth - 20, Math.max(0, (handleElement.offsetLeft - deltaX)))

        const value = min + Math.round(((left * (max - min)) / (slideBar.current.clientWidth - 20)) / step) * step

        if (handle === 'dragHandle') {
            const {value2} = state
            if (value < (value2 - diff)) {
                setState({left1: left, value1: value})
            }
        } else {
            const {value1} = state
            if (value > (value1 + diff)) {
                setState({left2: left, value2: value})
            }
        }
    }

    // Reports the values once a drag or a key press ends
    const commitChange = () => {
        const {props, state} = latest.current

        const {value1, value2} = state
        // A plain click on a handle doesn't change anything
        if (value1 !== props.value1 || value2 !== props.value2) {
            props.onChange?.(value1, value2)
        }
    }

    const closeDragElement = () => {
        // stop moving when mouse button is released:
        removeDragListeners()

        commitChange()
        setState({cursor: 'grab', isMoving: false})
    }

    // Arrow keys move the focused handle a step, Home/End as far as it goes; the handles keep diff apart like when
    // dragging. Enter/Space is the click on the handle. The value is reported on keyup (see commitChange), so holding
    // a key reports once instead of for every repeat.
    const onHandleKeyDown = (handle: DragHandle, event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!interactive) {
            return
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!event.repeat) {
                (handle === 'dragHandle' ? onClickValue1 : onClickValue2)?.()
            }
            return
        }

        const {props, state} = latest.current
        const {min, max, step, diff} = props
        const first = handle === 'dragHandle'
        const current = first ? state.value1 : state.value2
        // The range this handle may move in: the drag allows value1 < value2 - diff and value2 > value1 + diff
        const lowest = first ? min : state.value1 + diff + step
        const highest = first ? state.value2 - diff - step : max

        let value: number
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                value = current - step
                break
            case 'ArrowRight':
            case 'ArrowUp':
                value = current + step
                break
            case 'Home':
                value = lowest
                break
            case 'End':
                value = highest
                break
            default:
                return
        }

        event.preventDefault()
        value = Math.min(highest, Math.max(lowest, value))

        if (value !== current) {
            setState(first ? {value1: value, left1: valueToLeft(value)} : {value2: value, left2: valueToLeft(value)})
        }
    }

    const {left1, left2, value1, value2, cursor, isMoving} = state

    const {min, max} = props
    const {label1, label2} = valuesToLabel(value1, value2)

    const handleCursor = interactive ? cursor : 'default'

    return (
        // Fills the width of its parent between its margins: flex 1 in a row, as a block anywhere else
        <div style={{flex: 1, ...componentMargins.slider, ...style}}>
            {(label || info) &&
                <ControlLabel style={{marginBottom: controlLabel.gapAbove}} info={info} disabled={disabled}>
                    {label}
                </ControlLabel>
            }

            <div style={{height: 30}}>
                <div style={{height: 2, width: '100%', background: Colors.grey30, position: 'relative', marginTop: 13, userSelect: 'none'}} ref={slideBar}>
                    <div style={{height: 2, width: left2 - left1, background: disabled ? Colors.grey40 : Colors.buttonSolid, position: 'absolute', left: left1}}>
                    </div>
                    {startInclude &&
                        <div style={{height: 2, width: left1, background: Colors.grey50, position: 'absolute', left: 0}}></div>
                    }

                    {endInclude &&
                        <div style={{height: 2, width: `calc(100% - ${left2}px)`, background: Colors.grey40, position: 'absolute', right: 0}}></div>
                    }

                    <div
                        style={{height: 20, width: 20,
                            background: disabled ? Colors.grey20 : 'white', left: left1, top: -9, position: 'absolute', borderRadius: '50%', cursor: handleCursor,
                            boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0,0,0,.12)'
                        }}
                        ref={dragHandle}
                        className={focusableClass}
                        // Disabled is no tab stop, readOnly stays focusable to read the value
                        tabIndex={disabled ? -1 : 0}
                        role='slider'
                        aria-label={handleLabels?.[0]}
                        aria-valuenow={value1}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuetext={label1}
                        aria-disabled={disabled || undefined}
                        aria-readonly={readOnly || undefined}
                        onMouseUp={() => {
                            if (!isMoving && interactive) {
                                onClickValue1?.()
                            }
                        }}
                        onMouseDown={(event) => dragMouseDown('dragHandle', event)}
                        onKeyDown={(event) => onHandleKeyDown('dragHandle', event)}
                        onKeyUp={commitChange}
                        onBlur={commitChange}
                    >
                        {value1Icon &&
                            <i style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 12}} className={`mdi ${value1Icon}`} />
                        }
                        <div style={{marginTop: 20, userSelect: 'none', color: Colors.textMedium}}>{label1}</div>
                    </div>
                    <div
                        style={{height: 20, width: 20,
                            background: disabled ? Colors.grey20 : 'white', left: left2, top: -9, position: 'absolute', borderRadius: '50%', cursor: handleCursor,
                            boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0,0,0,.12)'
                        }}
                        ref={dragHandle2}
                        className={focusableClass}
                        // Disabled is no tab stop, readOnly stays focusable to read the value
                        tabIndex={disabled ? -1 : 0}
                        role='slider'
                        aria-label={handleLabels?.[1]}
                        aria-valuenow={value2}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuetext={label2}
                        aria-disabled={disabled || undefined}
                        aria-readonly={readOnly || undefined}
                        onMouseUp={() => {
                            if (!isMoving && interactive) {
                                onClickValue2?.()
                            }
                        }}
                        onMouseDown={(event) => dragMouseDown('dragHandle2', event)}
                        onKeyDown={(event) => onHandleKeyDown('dragHandle2', event)}
                        onKeyUp={commitChange}
                        onBlur={commitChange}
                    >
                        {value2Icon &&
                            <i style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 12}} className={`mdi ${value2Icon}`} />
                        }
                        <div style={{position: 'absolute', top: 20, right: 0, whiteSpace: 'nowrap', userSelect: 'none', color: Colors.textMedium}}>{label2}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Slider
